import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Package, Truck, Plane, Ship, MapPin, Loader2, IndianRupee, ArrowRight, Clock, CheckCircle2, Copy, Locate } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useIdempotentPost } from "@/hooks/useIdempotentPost";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useParams, Link, useSearchParams } from "react-router-dom";

const MODE_ICONS = { land: Truck, air: Plane, water: Ship };
const rupees = (paise) => `₹${(paise / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

export default function Parcels() {
  const { user } = useAuth();
  const [tab, setTab] = useState("book");
  const [myShipments, setMyShipments] = useState([]);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const cancelled = params.get("cancelled");
    if (!cancelled || !user) return;
    api.post(`/parcels/${cancelled}/cancel-payment`)
      .then(() => toast.info("Card payment cancelled. You were not charged."))
      .catch(() => {})
      .finally(() => setParams({}, { replace: true }));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user && tab === "mine") {
      api.get("/parcels/mine").then(r => setMyShipments(r.data)).catch(() => {});
    }
  }, [user, tab]);

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-5">
        <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1 rounded-full mb-3">
          <Package size={12} /> Send anywhere
        </div>
        <h1 className="heading text-4xl md:text-5xl font-extrabold text-stone-900 leading-tight">Parcel delivery</h1>
        <p className="text-stone-600 mt-2 max-w-xl">Book a shipment across land, air or water. Instant quote, live tracking, and pay via wallet or card.</p>
      </motion.div>

      <div className="flex gap-2 mb-5 border-b border-stone-200">
        {[{ k: "book", l: "Book" }, { k: "track", l: "Track a parcel" }, { k: "mine", l: "My shipments" }].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px ${tab === t.k ? "text-blue-700 border-blue-700" : "text-stone-500 border-transparent hover:text-stone-800"}`}
            data-testid={`parcels-tab-${t.k}`}
          >{t.l}</button>
        ))}
      </div>

      {tab === "book" && (user ? <BookForm /> : <div className="p-6 text-sm text-stone-600">Please <Link to="/" className="text-blue-700 font-bold">sign in</Link> to book a parcel.</div>)}
      {tab === "track" && <TrackLookup />}
      {tab === "mine" && (user ? <MyShipments shipments={myShipments} /> : <div className="p-6 text-sm text-stone-600">Sign in to see your shipments.</div>)}
    </div>
  );
}

// ---------- Book flow ----------

function BookForm() {
  const [f, setF] = useState({
    pickup_name: "", pickup_phone: "", pickup_address: "", pickup_city: "", pickup_lat: null, pickup_lng: null,
    drop_name: "", drop_phone: "", drop_address: "", drop_city: "", drop_lat: null, drop_lng: null,
    contents_summary: "documents", weight_grams: 500, declared_value_paise: 0, is_fragile: false,
    mode: "land", urgency: "standard", payment_method: "wallet",
  });
  const [quote, setQuote] = useState(null);
  const [booking, setBooking] = useState(false);
  const [result, setResult] = useState(null);

  const usePickupLoc = () => navigator.geolocation.getCurrentPosition(
    p => setF(x => ({ ...x, pickup_lat: p.coords.latitude, pickup_lng: p.coords.longitude })),
    () => toast.error("Location failed"),
  );
  const useDropLoc = () => navigator.geolocation.getCurrentPosition(
    p => setF(x => ({ ...x, drop_lat: p.coords.latitude, drop_lng: p.coords.longitude })),
    () => toast.error("Location failed"),
  );

  const canQuote = f.pickup_lat != null && f.drop_lat != null;
  useEffect(() => {
    if (!canQuote) { setQuote(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/parcels/quote", {
          pickup_lat: f.pickup_lat, pickup_lng: f.pickup_lng,
          drop_lat: f.drop_lat, drop_lng: f.drop_lng,
          weight_grams: parseInt(f.weight_grams) || 500, mode: f.mode, urgency: f.urgency,
        });
        setQuote(r.data);
      } catch (_) { setQuote(null); }
    }, 200);
    return () => clearTimeout(t);
  }, [f.pickup_lat, f.pickup_lng, f.drop_lat, f.drop_lng, f.weight_grams, f.mode, f.urgency]);

  const postOnce = useIdempotentPost("pb");
  const book = async (e) => {
    e.preventDefault();
    if (!canQuote) return toast.error("Pin both pickup and drop locations");
    if (!f.pickup_phone || !f.drop_phone) return toast.error("Both phones are required");
    setBooking(true);
    try {
      const r = await postOnce("/parcels/book", { ...f, weight_grams: parseInt(f.weight_grams), origin_url: window.location.origin });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url; return;
      }
      setResult(r.data);
      toast.success(`Booked! Tracking: ${r.data.tracking_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setBooking(false); }
  };

  if (result) {
    return (
      <div className="bg-white border-2 border-emerald-200 rounded-3xl p-6 text-center" data-testid="parcel-book-result">
        <div className="w-16 h-16 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-3"><CheckCircle2 size={32} className="text-emerald-700" /></div>
        <h2 className="heading text-3xl font-extrabold text-stone-900 mb-1">Booked!</h2>
        <p className="text-stone-600 mb-3">Your parcel is scheduled. Share the tracking code below with the recipient.</p>
        <div className="inline-flex items-center gap-2 bg-stone-900 text-white font-mono font-bold text-lg px-4 py-2 rounded-full">
          {result.tracking_id}
          <button onClick={() => { navigator.clipboard.writeText(result.tracking_id); toast.success("Copied"); }} data-testid="parcel-tracking-copy" className="hover:text-blue-300"><Copy size={14} /></button>
        </div>
        <div className="mt-6 flex gap-2 justify-center">
          <Link to={`/track/${result.tracking_id}`}><Button className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="parcel-track-btn">Track live</Button></Link>
          <Button variant="outline" className="rounded-full" onClick={() => setResult(null)} data-testid="parcel-book-another">Book another</Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={book} className="space-y-4" data-testid="parcel-book-form">
      {/* Pickup */}
      <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-widest font-bold text-blue-700">📍 Pickup from</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Input value={f.pickup_name} onChange={e => setF({ ...f, pickup_name: e.target.value })} placeholder="Sender name" required className="rounded-xl" data-testid="pickup-name" />
          <Input value={f.pickup_phone} onChange={e => setF({ ...f, pickup_phone: e.target.value })} placeholder="Sender phone" required className="rounded-xl" data-testid="pickup-phone" />
          <Input value={f.pickup_city} onChange={e => setF({ ...f, pickup_city: e.target.value })} placeholder="City" required className="rounded-xl" data-testid="pickup-city" />
          <Button type="button" variant="outline" onClick={usePickupLoc} className="rounded-xl" data-testid="pickup-locate"><Locate size={14} className="mr-1.5" /> Pin pickup</Button>
        </div>
        <Textarea value={f.pickup_address} onChange={e => setF({ ...f, pickup_address: e.target.value })} placeholder="Full pickup address" rows={2} required className="rounded-xl" data-testid="pickup-address" />
        {f.pickup_lat != null && <div className="text-[11px] text-emerald-700 font-bold">📍 Pinned</div>}
      </div>

      {/* Drop */}
      <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-widest font-bold text-blue-700">📦 Deliver to</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Input value={f.drop_name} onChange={e => setF({ ...f, drop_name: e.target.value })} placeholder="Recipient name" required className="rounded-xl" data-testid="drop-name" />
          <Input value={f.drop_phone} onChange={e => setF({ ...f, drop_phone: e.target.value })} placeholder="Recipient phone" required className="rounded-xl" data-testid="drop-phone" />
          <Input value={f.drop_city} onChange={e => setF({ ...f, drop_city: e.target.value })} placeholder="City" required className="rounded-xl" data-testid="drop-city" />
          <Button type="button" variant="outline" onClick={useDropLoc} className="rounded-xl" data-testid="drop-locate"><Locate size={14} className="mr-1.5" /> Pin drop</Button>
        </div>
        <Textarea value={f.drop_address} onChange={e => setF({ ...f, drop_address: e.target.value })} placeholder="Full delivery address" rows={2} required className="rounded-xl" data-testid="drop-address" />
        {f.drop_lat != null && <div className="text-[11px] text-emerald-700 font-bold">📍 Pinned</div>}
      </div>

      {/* Package */}
      <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-widest font-bold text-blue-700">📃 What&apos;s in the box?</div>
        <div className="grid md:grid-cols-4 gap-3">
          <Input value={f.contents_summary} onChange={e => setF({ ...f, contents_summary: e.target.value })} placeholder="Contents" className="rounded-xl md:col-span-2" data-testid="parcel-contents" />
          <Input type="number" value={f.weight_grams} onChange={e => setF({ ...f, weight_grams: e.target.value })} placeholder="Weight (g)" className="rounded-xl" data-testid="parcel-weight" />
          <label className="flex items-center gap-2 text-xs text-stone-700">
            <input type="checkbox" checked={f.is_fragile} onChange={e => setF({ ...f, is_fragile: e.target.checked })} className="accent-blue-600" data-testid="parcel-fragile" />
            Fragile
          </label>
        </div>
      </div>

      {/* Mode + urgency */}
      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1.5 block">Mode</label>
          <div className="grid grid-cols-3 gap-1">
            {["land", "air", "water"].map(m => {
              const Icon = MODE_ICONS[m];
              return (
                <button key={m} type="button" onClick={() => setF({ ...f, mode: m })}
                  className={`py-2 rounded-xl border-2 text-xs font-bold capitalize flex flex-col items-center gap-0.5 ${f.mode === m ? "border-blue-600 bg-blue-50 text-blue-700" : "border-stone-200 hover:border-blue-300"}`}
                  data-testid={`parcel-mode-${m}`}
                >
                  <Icon size={16} /> {m}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1.5 block">Urgency</label>
          <Select value={f.urgency} onValueChange={v => setF({ ...f, urgency: v })}>
            <SelectTrigger className="rounded-xl" data-testid="parcel-urgency"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">Standard (1×)</SelectItem>
              <SelectItem value="express">Express (1.5×)</SelectItem>
              <SelectItem value="priority">Priority (2×)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1.5 block">Pay with</label>
          <Select value={f.payment_method} onValueChange={v => setF({ ...f, payment_method: v })}>
            <SelectTrigger className="rounded-xl" data-testid="parcel-payment"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="wallet">Find2Near wallet</SelectItem>
              <SelectItem value="stripe">Card (Stripe)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Quote + submit */}
      {quote && (
        <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 flex flex-wrap items-center gap-4" data-testid="parcel-quote">
          <div className="flex-1 min-w-[140px]">
            <div className="text-xs text-stone-500">Quote</div>
            <div className="text-2xl font-black text-blue-700 flex items-center gap-1"><IndianRupee size={20} />{(quote.total_paise / 100).toFixed(0)}</div>
          </div>
          <div className="flex-1 min-w-[100px] text-xs">
            <div className="text-stone-500">Distance</div>
            <div className="font-bold">{quote.distance_km} km</div>
          </div>
          <div className="flex-1 min-w-[100px] text-xs">
            <div className="text-stone-500 flex items-center gap-1"><Clock size={10} /> ETA</div>
            <div className="font-bold">{quote.eta_hours} hrs</div>
          </div>
        </motion.div>
      )}

      <Button type="submit" disabled={booking || !quote} className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700 font-bold text-base" data-testid="parcel-book-submit">
        {booking ? <><Loader2 size={16} className="animate-spin mr-2" /> Booking…</> : quote ? <>Book for {rupees(quote.total_paise)} <ArrowRight size={16} className="ml-1" /></> : "Pin both locations to see quote"}
      </Button>
    </form>
  );
}

// ---------- Track lookup ----------

function TrackLookup() {
  const [code, setCode] = useState("");
  return (
    <div className="max-w-md mx-auto space-y-3">
      <Input value={code} onChange={e => setCode(e.target.value.toUpperCase().trim())} placeholder="e.g. FN23ABCDEF" className="rounded-xl h-12 font-mono text-center text-lg" data-testid="track-input" />
      <Link to={`/parcels/track/${code}`}><Button disabled={!code} className="w-full rounded-full bg-blue-600 hover:bg-blue-700" data-testid="track-go">Track this parcel</Button></Link>
    </div>
  );
}

// ---------- My shipments ----------

function MyShipments({ shipments }) {
  if (shipments.length === 0) return <div className="p-8 text-center text-stone-500">No shipments yet.</div>;
  return (
    <div className="space-y-2" data-testid="my-shipments">
      {shipments.map(s => (
        <Link key={s.shipment_id} to={`/parcels/track/${s.tracking_id}`} className="block bg-white border border-stone-200 rounded-2xl p-4 hover:border-blue-500 transition-colors" data-testid={`shipment-${s.tracking_id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="font-mono font-bold text-blue-700">{s.tracking_id}</div>
              <div className="text-xs text-stone-500 truncate">{s.pickup.city} <ArrowRight size={10} className="inline" /> {s.drop.city} · {s.package.contents_summary}</div>
            </div>
            <StatusBadge status={s.status} />
            <div className="text-sm font-bold text-stone-900">{rupees(s.quote.total_paise)}</div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    created:          "bg-stone-100 text-stone-700",
    picked_up:        "bg-blue-100 text-blue-700",
    in_transit:       "bg-amber-100 text-amber-800",
    out_for_delivery: "bg-purple-100 text-purple-800",
    delivered:        "bg-emerald-100 text-emerald-800",
    cancelled:        "bg-red-100 text-red-800",
  };
  return <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${map[status] || "bg-stone-100"}`}>{status.replace(/_/g, " ")}</span>;
}

// ---------- Public tracking page ----------

const STATUS_STEPS = ["created", "picked_up", "in_transit", "out_for_delivery", "delivered"];

export function ParcelTracking() {
  const { trackingId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newStatus, setNewStatus] = useState("");

  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const refresh = async () => {
    if (sessionId) await api.get(`/payments/status/${sessionId}`).catch(() => {});
    api.get(`/parcels/track/${trackingId}`)
      .then(r => { setData(r.data); setError(null); })
      .catch(e => setError(e?.response?.data?.detail || "Not found"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); const iv = setInterval(refresh, 15000); return () => clearInterval(iv); }, [trackingId]);

  const addEvent = async () => {
    try {
      await api.post(`/parcels/${trackingId}/events`, { status: newStatus, note: "Manual update" });
      toast.success("Status updated");
      setNewStatus("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  if (loading) return <div className="p-12 text-center text-stone-500"><Loader2 size={16} className="inline animate-spin mr-2" /> Loading…</div>;
  if (error || !data) return <div className="p-12 text-center text-stone-500">{error || "Not found"}</div>;

  const s = data.shipment;
  const currentIdx = Math.max(0, STATUS_STEPS.indexOf(s.status));

  return (
    <div className="max-w-3xl mx-auto px-5 py-6">
      <div className="bg-white border border-stone-200 rounded-3xl overflow-hidden" data-testid="parcel-tracking">
        <div className="p-5 md:p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-b border-stone-200">
          <div className="text-[10px] uppercase tracking-widest font-bold text-blue-700">Tracking</div>
          <div className="font-mono text-2xl md:text-3xl font-black text-stone-900">{s.tracking_id}</div>
          <div className="text-sm text-stone-600 mt-1">{s.pickup.city} <ArrowRight size={12} className="inline" /> {s.drop.city} · {s.package.contents_summary}</div>
          <div className="mt-3"><StatusBadge status={s.status} /></div>
        </div>

        {/* Stepper */}
        <div className="p-5 md:p-6 border-b border-stone-100">
          <div className="grid grid-cols-5 gap-1" data-testid="parcel-stepper">
            {STATUS_STEPS.map((step, idx) => (
              <div key={step} className="text-center">
                <div className={`w-8 h-8 rounded-full mx-auto grid place-items-center text-xs font-black ${idx <= currentIdx ? "bg-blue-600 text-white" : "bg-stone-100 text-stone-400"}`}>{idx + 1}</div>
                <div className={`text-[9px] uppercase tracking-widest font-bold mt-1 ${idx <= currentIdx ? "text-blue-700" : "text-stone-400"}`}>{step.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Event timeline */}
        <div className="p-5 md:p-6">
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-3">Timeline</div>
          <div className="space-y-3">
            {data.events.slice().reverse().map(e => (
              <div key={e.event_id} className="flex gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-bold text-stone-900 capitalize">{e.status.replace(/_/g, " ")}</div>
                  <div className="text-xs text-stone-500">{new Date(e.created_at).toLocaleString()} · {e.location?.city || "—"}</div>
                  {e.note && <div className="text-xs text-stone-600 mt-0.5">{e.note}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Manual update (only shows to signed-in sender/admin) */}
        <div className="border-t border-stone-100 p-4 bg-stone-50 flex items-center gap-2" data-testid="parcel-manual-update">
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger className="rounded-xl h-9 text-sm flex-1"><SelectValue placeholder="Update status…" /></SelectTrigger>
            <SelectContent>{STATUS_STEPS.concat(["cancelled"]).map(x => <SelectItem key={x} value={x}>{x.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
          <Button disabled={!newStatus} onClick={addEvent} className="rounded-full bg-blue-600 hover:bg-blue-700 text-xs h-9" data-testid="parcel-update-btn">Add event</Button>
        </div>
      </div>
    </div>
  );
}
